import * as React from "react";
import { useRef } from "react";
import { filtersResx } from "~/resxDist/filtersResx";
import { AddLink } from "~/sharedComponents/buttons";
import { ItemKey } from "../list";
import { FiltersGroupRow } from "../listRows/FiltersGroupRow";
import { Popover } from "../popover/Popover";
import { Checkbox, checkboxTheme } from "./Checkbox";
import { MultiCheckbox, MultiCheckboxProps, OptionType } from "./MultiCheckbox";
import * as styles from "./MultiCheckbox.less";
import { getItemsToShow } from "./ShowMoreHelper";

export type ItemsToShow = F3<OptionType[], number, number[], OptionType[]>;

interface MultiCheckboxWithShowMoreProps extends MultiCheckboxProps {
    initItemsToShow: OptionType[];
    sourceLoaded?: boolean;
    loadAction?: F;
    groupBy?: F1<OptionType[], OptionType[]>;
    filterItem?: (item: OptionType, searchText: string) => boolean;
    getItemValue?: F1<OptionType, ItemKey>;
}

const getDefaultItemValue = (item: OptionType) => item.id;

export const MultiCheckboxWithShowMore: React.FC<MultiCheckboxWithShowMoreProps> = props => {
    const {
        items,
        initItemsToShow,
        onChangeAction,
        isVirtualized,
        sourceLoaded = true,
        loadAction,
        groupBy,
        filterItem,
        getItemValue = getDefaultItemValue,
        getTexForItem,
        withHierarchy,
    } = props;
    const initViewItems = groupBy ? groupBy(initItemsToShow) : initItemsToShow;
    const [viewItems, setViewItems] = React.useState<OptionType[]>(initViewItems);

    const isFirstRun = useRef(true);
    React.useEffect(() => {
        if (isFirstRun.current) {
            isFirstRun.current = false;
            return;
        }

        setViewItems(prevRecent => getItemsToShow(prevRecent, items, groupBy));
    }, [items]);

    const onToggle = (state: boolean) => {
        if (state && !sourceLoaded && loadAction) {
            loadAction();
        }
    };

    const getCheckbox: React.FC<OptionType> = item => (
        <Checkbox
            key={`${getItemValue(item)}_checkbox_filter`}
            checked={item.checked}
            onChange={newValue => onChangeAction(item, newValue)}
            theme={checkboxTheme.ellipsis}
            title={item.label}
            blockForFullStory={true}
        >
            {item.label}
        </Checkbox>
    );

    const showPopoverTrigger = React.useMemo(() => items?.length > initItemsToShow.length, []);

    return (
        <>
            {viewItems.map(item =>
                item.group ? <FiltersGroupRow key={item.label} item={item} /> : getCheckbox(item as OptionType)
            )}
            {showPopoverTrigger && (
                <Popover openedClass={styles.popup_wrapper} onToggle={onToggle} width={270} renderToBody={true}>
                    <AddLink tabIndex={0} text={filtersResx.More_Text} className={styles.add_btn} />
                    <MultiCheckbox
                        filterItem={filterItem}
                        items={items}
                        groupBy={groupBy}
                        onChangeAction={onChangeAction}
                        isVirtualized={isVirtualized}
                        pageSize={12}
                        blockForFullStory={props.blockForFullStory}
                        isLoading={!sourceLoaded}
                        getTexForItem={getTexForItem}
                        withHierarchy={withHierarchy}
                    />
                </Popover>
            )}
        </>
    );
};
